// Web port of mobile/src/components/alert-cards/alert-utils.ts and
// mobile/src/components/alerts-section/{event-icons,severity-colors}.ts.
// Logic is identical so the two surfaces stay in sync as the alert
// pipeline evolves on the backend.

import type { FeedAlert } from '../../types';

// --- Severity ---------------------------------------------------------------

export type AlertLevel = 'RED' | 'ORANGE' | 'YELLOW';

export interface SeverityPalette {
  readonly core: string;
  readonly bg: string;
  readonly border: string;
}

export const SEVERITY_COLORS: Record<AlertLevel, SeverityPalette> = {
  RED: {
    core: '#e05555',
    bg: 'rgba(224, 85, 85, 0.16)',
    border: 'rgba(224, 85, 85, 0.45)',
  },
  ORANGE: {
    core: '#FF5C28',
    bg: 'rgba(255, 92, 40, 0.14)',
    border: 'rgba(255, 92, 40, 0.38)',
  },
  YELLOW: {
    core: '#f0a030',
    bg: 'rgba(240, 160, 48, 0.14)',
    border: 'rgba(240, 160, 48, 0.35)',
  },
};

// Mirrors mobile/src/components/alerts-section/severity-colors.ts —
// SEVERITY_COLORS stays purely visual; consumers call
// `t(SEVERITY_LABEL_KEYS[level])` to get the localized chip label.
//
// `as const satisfies Record<AlertLevel, string>` narrows each entry
// to its own literal-string type so the typed-options ParseKeys union
// at the `t()` call site can resolve them — `Record<AlertLevel,
// ParseKeys>` would explode (TS2590) because the union grew past
// TypeScript's complexity threshold once both mobile + web key trees
// landed in shared/i18n/locales/en.json. The literal-narrowing form
// keeps the call site happy WITHOUT exposing the full ParseKeys union
// here.
//
// Typo safety on the literal values is restored statically by
// `shared/i18n/scripts/lint-call-sites.ts`, which scans every
// `_KEYS = {` declaration under mobile/src + web/src and validates the
// values against en.json. A misspelled key (e.g.
// 'alerts.severitiy.emergency') compiles fine here but fails the
// `i18n validate` CI check before merge.
export const SEVERITY_LABEL_KEYS = {
  RED: 'alerts.severity.emergency',
  ORANGE: 'alerts.severity.warning',
  YELLOW: 'alerts.severity.advisory',
} as const satisfies Record<AlertLevel, string>;

export function tierToLevel(tier: FeedAlert['severityTier']): AlertLevel {
  if (tier === 1) return 'RED';
  if (tier === 2) return 'ORANGE';
  return 'YELLOW';
}

// --- Source labels + branding ----------------------------------------------

export const SOURCE_LABELS: Record<string, string> = {
  krisinformation: 'Krisinformation',
  smhi: 'SMHI',
  sr_vma: 'Sveriges Radio',
  polisen: 'Polisen',
};

export function formatSource(sourceApi: string): string {
  return (
    SOURCE_LABELS[sourceApi] ??
    sourceApi.charAt(0).toUpperCase() + sourceApi.slice(1)
  );
}

export interface SourceBranding {
  readonly country: string;
  /** URL of the wordmark image. Served from web/public/alert-sources/.
   *  These are the same PNGs the mobile app bundles at
   *  mobile/src/assets/alert-sources/{source}-wordmark.png. */
  readonly wordmarkUrl?: string;
  /** URL of the wide banner image, used in the detail-modal hero band. */
  readonly bannerUrl?: string;
  /** Solid background for the 44×44 logo tile and the modal banner. Most
   *  Swedish alert wordmarks read best on a navy/blue tile in both
   *  themes. */
  readonly tileBg?: string;
  readonly bannerBg?: string;
  /** When true, apply `filter: brightness(0) invert(1)` so a
   *  black-on-transparent wordmark renders white on the navy tile.
   *  Mirrors mobile's `wordmarkTintDark` flag for SMHI / SR VMA. */
  readonly tintWhite?: boolean;
}

// Branding metadata for known alert sources. Asset URLs mirror
// mobile/src/assets/alert-sources/* so the visual treatment matches.
export const SOURCE_BRANDING: Record<string, SourceBranding> = {
  krisinformation: {
    country: 'Sweden',
    wordmarkUrl: '/alert-sources/krisinformation-wordmark.png',
    bannerUrl: '/alert-sources/krisinformation-banner.png',
    bannerBg: '#0E2147',
    tileBg: '#0E2147',
  },
  smhi: {
    country: 'Sweden',
    wordmarkUrl: '/alert-sources/smhi-wordmark.png',
    bannerUrl: '/alert-sources/smhi-banner.jpg',
    bannerBg: '#0E2147',
    tileBg: '#0E2147',
    tintWhite: true,
  },
  sr_vma: {
    country: 'Sweden',
    wordmarkUrl: '/alert-sources/sr-vma-wordmark.png',
    bannerUrl: '/alert-sources/sr-vma-banner.jpg',
    bannerBg: '#0E2147',
    tileBg: '#0E2147',
    tintWhite: true,
  },
  polisen: {
    country: 'Sweden',
    wordmarkUrl: '/alert-sources/polisen-wordmark.png',
    bannerUrl: '/alert-sources/polisen-banner.jpg',
    bannerBg: '#1862a8',
    tileBg: '#1862a8',
  },
};

// --- Area resolver ----------------------------------------------------------

function firstNonBlank(values: readonly string[] | undefined): string | null {
  if (!values) return null;
  for (const v of values) {
    if (v && v.trim().length > 0) return v.trim();
  }
  return null;
}

export function resolveAlertArea(alert: FeedAlert): string {
  if (alert.areaName && alert.areaName.trim().length > 0) {
    return alert.areaName.trim();
  }
  const fromGeo = firstNonBlank(alert.geographicArea);
  if (fromGeo) return fromGeo;
  const fromAffected = firstNonBlank(alert.affectedAreaNames);
  if (fromAffected) return fromAffected;
  return SOURCE_BRANDING[alert.sourceApi]?.country ?? '';
}

// --- Time-ago (mirrors mobile's helpers) -----------------------------------

export function timeAgo(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  const mins = Math.max(0, Math.floor((now.valueOf() - t.valueOf()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatRange(fromIso?: string | null, toIso?: string | null): string | null {
  if (!fromIso && !toIso) return null;
  const fmt = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return '';
    const day = d
      .toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' })
      .toUpperCase();
    const time = d.toLocaleTimeString('en', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${day} ${time}`;
  };
  if (fromIso && toIso) return `${fmt(fromIso)} → ${fmt(toIso)}`;
  if (fromIso) return `From ${fmt(fromIso)}`;
  return `Until ${fmt(toIso!)}`;
}
